import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  Button,
  FormController,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Typography,
  Alert
} from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import { AddToProductDetails } from '../AddToProductDetails/AddToProductDetails';
import {
  TOO_MANY_PRODUCTS_MESSAGE,
  INTENT_NOT_QUOTEABLE_MESSAGE,
  MAX_QUOTE_ITEMS,
  ADD_TO_LAST_SELECTION_KEY,
  SCREENS,
  TRACK
} from '../../AddToConstants';
import { AddToCoreContext } from '../../core/AddToCoreContext';
import { triggerAnalytics } from '../../AddToHelpers';

const CARD_CLASS = 'sui-border-1 sui-border-primary sui-border-solid sui-rounded-lg sui-p-3 sui-mb-3';

export const AddToSelection = ({
  products, addToType, setAddToType, handleClose, isProjectsEligible, copyProjectData,
}) => {
  // isProjectsEligible = customerType === B2B
  const showAddToQuoteButton = useConfigService('fs:isATQEnabled');
  const hasCatalogItems = products?.some((product) => product.itemType !== 'intent');
  const { setScreen } = useContext(AddToCoreContext);

  const isOptionVisible = (value) => {
    if (value === 'Project') return isProjectsEligible;
    if (value === 'Quote') return showAddToQuoteButton && isProjectsEligible;
    if (value === 'List') return true;
    return false;
  };

  const getInitialSelection = () => {
    try {
      const stored = localStorage.getItem(ADD_TO_LAST_SELECTION_KEY);
      if (stored && isOptionVisible(stored)) {
        return stored;
      }
    } catch (err) {
      // localStorage unavailable
    }
    return addToType;
  };

  const [selectedValue, setSelectedValue] = useState(getInitialSelection);
  const [quoteBlockedMsg, setQuoteBlockedMsg] = useState('');

  const handleChange = (event) => {
    const { value } = event.target;
    setSelectedValue(value);
    setQuoteBlockedMsg('');
    try {
      localStorage.setItem(ADD_TO_LAST_SELECTION_KEY, value);
    } catch (err) {
      // localStorage unavailable — continue without persisting
    }
  };

  const handleSubmit = () => {
    if (!selectedValue) return;
    if (selectedValue === 'Quote' && !hasCatalogItems) {
      setQuoteBlockedMsg(INTENT_NOT_QUOTEABLE_MESSAGE);
      return;
    }
    if (selectedValue === 'Project' && copyProjectData?.length) {
      setAddToType('CopyProject');
    } else {
      setAddToType(selectedValue);
    }
  };

  const projectCardClass = classNames(
    CARD_CLASS,
    {
      'sui-hidden': !isProjectsEligible,
      'sui-shadow-sm': selectedValue === 'Project',
    }
  );

  return (
    <>
      <DrawerHeader
        title="Add to..."
        onClose={handleClose}
      />
      <DrawerBody>
        <>
          <AddToProductDetails products={products} />
          <div className="sui-w-full sui-mt-4">
            <FormController fullWidth>
              <RadioGroup
                aria-labelledby="add-to-buttons-group-label"
                value={selectedValue}
                name="add-to-radio-buttons-group"
                onChange={handleChange}
              >
                <div
                  className={projectCardClass}
                  data-testid="project-radio-button"
                >
                  <SelectionControlLabel
                    value="Project"
                    label={(
                      <div>
                        <Typography variant="body-sm" weight="bold">Projects</Typography>
                        <Typography variant="body-xs" color="subtle">
                          Your hub to organize and track progress for your jobs in one place
                        </Typography>
                      </div>
                    )}
                  >
                    <Radio />
                  </SelectionControlLabel>
                </div>
                <div className={classNames(CARD_CLASS, { 'sui-shadow-sm': selectedValue === 'List' })}>
                  <SelectionControlLabel
                    value="List"
                    label={(
                      <div>
                        <Typography variant="body-sm" weight="bold">Lists</Typography>
                        <Typography variant="body-xs" color="subtle">
                          Build reusable templates and custom catalogs for frequently purchased materials
                        </Typography>
                      </div>
                    )}
                  >
                    <Radio />
                  </SelectionControlLabel>
                </div>
              </RadioGroup>
            </FormController>
            { (showAddToQuoteButton && isProjectsEligible) && (
              <FormController fullWidth>
                <RadioGroup
                  aria-labelledby="add-to-buttons-group-label-2"
                  value={selectedValue}
                  name="add-to-radio-buttons-group-2"
                  onChange={handleChange}
                >
                  {products?.length > MAX_QUOTE_ITEMS && (
                    <div className="sui-pt-4">
                      <Alert
                        data-testid="products-selection-alert"
                        status="error"
                      >
                        {TOO_MANY_PRODUCTS_MESSAGE}
                      </Alert>
                    </div>
                  )}
                  {quoteBlockedMsg && (
                    <div className="sui-pt-4">
                      <Alert
                        data-testid="quote-blocked-alert"
                        status="error"
                      >
                        {quoteBlockedMsg}
                      </Alert>
                    </div>
                  )}
                  <div className={classNames(CARD_CLASS, { 'sui-shadow-sm': selectedValue === 'Quote' })}>
                    <SelectionControlLabel
                      disabled={products?.length > MAX_QUOTE_ITEMS}
                      value="Quote"
                      label={(
                        <div>
                          <Typography variant="body-sm" weight="bold">Quotes</Typography>
                          <Typography
                            variant="body-xs"
                            color={products?.length > MAX_QUOTE_ITEMS ? 'inactive' : 'subtle'}
                          >
                            Lock in pricing as you finalize costs and make purchasing decisions.
                          </Typography>
                        </div>
                      )}
                    >
                      <Radio />
                    </SelectionControlLabel>
                  </div>
                </RadioGroup>
              </FormController>
            )}
            {isProjectsEligible && (
              <div className="sui-mt-4 sui-text-center">
                <Button
                  variant="text"
                  onClick={() => {
                    triggerAnalytics(TRACK.CHOOSING_THE_RIGHT_TOOL_OVERLAY);
                    setScreen(SCREENS.CHOOSING_THE_RIGHT_TOOL);
                  }}
                  endIcon={ArrowForward}
                >
                  Help me choose
                </Button>
              </div>
            )}
          </div>
        </>
      </DrawerBody>
      <DrawerFooter>
        <div className="sui-flex sui-flex-col sui-gap-4 sui-w-full">
          <Button
            fullWidth
            disabled={!selectedValue}
            onClick={handleSubmit}
          >
            Add to
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

AddToSelection.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  copyProjectData: PropTypes.arrayOf(PropTypes.shape({})),
  addToType: PropTypes.string,
  setAddToType: PropTypes.func,
  handleClose: PropTypes.func,
  isProjectsEligible: PropTypes.bool
};

AddToSelection.displayName = 'AddToSelection';

AddToSelection.defaultProps = {
  products: [],
  copyProjectData: [],
  addToType: null,
  setAddToType: () => {
  },
  handleClose: () => {
  },
  isProjectsEligible: false
};
